/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICommerceApiSettings,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { generateCacheKey } from '@msdyn365-commerce-modules/retail-actions';
import { IArticle } from './DataServiceEntities.g';
import { getAllArticlesAsync } from './DataActionExtension.g';

/** A distinct article tag and the number of articles carrying it. */
export interface IArticleTagResult {
    tag: string;
    count: number;
}

/** Upper bound on articles scanned to build the tag universe for the refiner panel. */
const MAX_ARTICLES_FOR_TAGS: number = 1000;

/**
 * Action input for deriving the distinct article tags (with counts) used by the refiner panel.
 * Tags are derived from the articles within the authored base-filter scope (filterTags).
 */
export class GetArticleTagsInput implements IActionInput {
    public apiSettings: ICommerceApiSettings;

    public locale?: string;

    /** Authored base filter (AND); the tag universe is computed within this scope. */
    public filterTags?: string;

    public constructor(apiSettings: ICommerceApiSettings, locale?: string, filterTags?: string) {
        this.apiSettings = apiSettings;
        this.locale = locale;
        this.filterTags = filterTags;
    }

    public getCacheKey = () =>
        generateCacheKey(this.apiSettings, { key: 'Locale', value: this.locale }, { key: 'FilterTags', value: this.filterTags || '' });

    public getCacheObjectType = () => 'ArticleTagsResult';

    public dataCacheType = (): CacheType => 'application';
}

/**
 * Builds the input from module config.
 * @param args - The API arguments.
 * @returns The action input.
 */
const createInput = (args: ICreateActionContext<{ filterByTags?: string[] }>): IActionInput => {
    const filterTags = args.config?.filterByTags && args.config.filterByTags.length > 0 ? args.config.filterByTags.join(',') : undefined;
    return new GetArticleTagsInput(args.requestContext.apiSettings, args.requestContext.locale, filterTags);
};

/**
 * Fetches lightweight article metadata and aggregates the distinct tags with their counts.
 * @param input - The input.
 * @param context - The action context.
 * @returns The distinct tags sorted by count (desc) then name.
 */
async function action(input: GetArticleTagsInput, context: IActionContext): Promise<IArticleTagResult[]> {
    const articles: IArticle[] = await getAllArticlesAsync(
        {
            callerContext: context,
            queryResultSettings: { Paging: { Top: MAX_ARTICLES_FOR_TAGS, Skip: 0 } }
        },
        input.filterTags
    );

    const counts = new Map<string, number>();
    articles.forEach(article => {
        if (!article.FilterKeywords) {
            return;
        }
        article.FilterKeywords.split(',')
            .map(tag => tag.trim())
            .filter(tag => tag.length > 0)
            .forEach(tag => {
                counts.set(tag, (counts.get(tag) || 0) + 1);
            });
    });

    return Array.from(counts.entries())
        .map(([tag, count]) => ({ tag, count }))
        .sort((first, second) => second.count - first.count || first.tag.localeCompare(second.tag));
}

export const getArticleTagsDataAction = createObservableDataAction({
    id: 'lobas-article-collection/get-article-tags',
    action: action as IAction<IArticleTagResult[]>,
    input: createInput
});

export default getArticleTagsDataAction;
